import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowRight, ArrowLeft, AlertTriangle } from "lucide-react";
import { API, FARO_LOGO } from "@/lib/faro";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Mínimo 8 caracteres");
    if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
      return toast.error("Debe incluir al menos una letra y un número");
    }
    if (form.password !== form.confirm) return toast.error("Las contraseñas no coinciden");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: form.password });
      toast.success("Contraseña actualizada. Inicia sesión");
      setTimeout(() => nav("/admin/login"), 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--faro-blue-deep)] faro-noise px-5" data-testid="admin-reset-page">
      <div className="relative w-full max-w-md">
        <Link to="/admin/login" className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white mb-6">
          <ArrowLeft size={14} /> Volver al login
        </Link>
        <div className="rounded-3xl bg-white p-8 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden border border-[rgba(30,42,143,0.15)]">
              <img src={FARO_LOGO} alt="Faro" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-display font-black text-lg text-[var(--faro-blue)]">Panel Faro</div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--faro-red)]">Nueva contraseña</div>
            </div>
          </div>

          <h1 className="font-display font-black text-2xl mt-6 flex items-center gap-2">
            <Lock size={20} className="text-[var(--faro-blue)]" /> Crea tu nueva contraseña
          </h1>

          {!token ? (
            <div className="mt-6 p-4 rounded-2xl bg-red-50 border border-red-200 flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-600 shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                Enlace incompleto. Pide un nuevo enlace desde <Link to="/admin/forgot-password" className="underline font-bold">¿Olvidaste tu contraseña?</Link>.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)]">Nueva contraseña</span>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="reset-password"
                  className="mt-1 w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-3 outline-none focus:border-[var(--faro-blue)]"
                  placeholder="Mínimo 8 caracteres, con letra y número"
                  required
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)]">Confirmar contraseña</span>
                <input
                  type="password"
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  data-testid="reset-confirm"
                  className="mt-1 w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-3 outline-none focus:border-[var(--faro-blue)]"
                  placeholder="Repite la contraseña"
                  required
                />
              </label>
              <button type="submit" disabled={loading} data-testid="reset-submit" className="btn-pill btn-primary w-full justify-center disabled:opacity-60">
                {loading ? "Guardando..." : "Guardar y entrar"} <ArrowRight size={16} />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
